import {
  ArrowUpOutlined,
  BarsOutlined,
  ClearOutlined,
  DeleteOutlined,
  HolderOutlined,
  InfoCircleOutlined,
  RedoOutlined,
  UndoOutlined,
} from '@ant-design/icons'
import {
  type CollisionDetection,
  defaultDropAnimationSideEffects,
  DndContext,
  DragEndEvent,
  DragOverlay,
  KeyboardSensor,
  PointerSensor,
  pointerWithin,
  type UniqueIdentifier,
  useDroppable,
  useSensor,
  useSensors,
} from '@dnd-kit/core'
import { restrictToVerticalAxis } from '@dnd-kit/modifiers'
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable'
import { CSS } from '@dnd-kit/utilities'
import { useVirtualizer } from '@tanstack/react-virtual'
import { Button, Collapse, Divider, Modal, Tag, Tooltip } from 'antd'
import React, {
  memo,
  type ReactNode,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react'
import { toast } from 'sonner'

import Loader from '@/components/Atoms/Loader/Loader'
import { formatCharacteristicValue } from '@/components/Filters/Filters.utils'
import { PrioritySaveBar } from '@/components/PrioritySortShared/PrioritySaveBar/PrioritySaveBar'
import {
  getComparableState,
  getEffectivePriority,
  hasComparableStateChanged,
  normalizePriorityOrder,
} from '@/components/PrioritySortShared/priorityState'
import { MAX_PRIORITY, TYPE_LABELS } from '@/constants'
import { useUserInfoStore } from '@/stores/user/userInfoStore'
import api from '@/utils/api/api'
import { handleApiError } from '@/utils/api/errors/handleApiError'
import { cleanTechnicalSuffix } from '@/utils/catalog/cleanDescription'
import {
  collapseSlottedOptionsForPriority,
  isSlottedCharacteristicType,
} from '@/utils/search/characteristics/slottedCharacteristics'

import cn from './ManualPriorityConfig.module.scss'
import {
  type CategoryPriorityUpdate,
  isPriorityCharacteristic,
  isPriorityValueOption,
  type PriorityCategoryTree,
  type PriorityCategoryTreesResponse,
  type PriorityCharacteristic,
  type PriorityConfigItem,
  type PriorityHistoryEntry,
  type PriorityItem,
  type PriorityItemId,
  type PriorityManagerProps,
  type PriorityRowData,
  type PriorityRowProps,
  type PriorityValuesModalContent,
  type ValuePriorityUpdate,
} from './ManualPriorityConfig.types'

const ESTIMATED_ROW_HEIGHT = 60
const AUTO_SCROLL_HOLD_MS = 500
const AUTO_SCROLL_EDGE_THRESHOLD = 80
const AUTO_SCROLL_STEP_MAX = 14
const AUTO_SCROLL_INTERVAL_MS = 16
const MAIN_ZONE_ID = 'main-zone'
const OTHERS_ZONE_ID = 'others-zone'

const getItemKey = (item: PriorityItem) =>
  String(item.id ?? item.code ?? item.value)

const RowItem = memo(
  ({
    data,
    index,
    type,
    isConfigured,
    isChanged,
    hasValueChanges,
    onReset,
    onPrioritize,
    onOpenValues,
    isOverlay = false,
    dragProps = {},
  }: PriorityRowProps<PriorityRowData>) => {
    const name = useMemo(
      () =>
        type === 'char'
          ? cleanTechnicalSuffix(data?.name || '')
              .replace(/\s*SOS\s*/gi, ' ')
              .trim()
          : formatCharacteristicValue(String(data?.value ?? '')),
      [type, data?.name, data?.value]
    )

    const unit = useMemo(
      () =>
        type === 'value' && data?.unitOfMeasurementDto?.description
          ? ` [${data.unitOfMeasurementDto.description}]`
          : '',
      [type, data?.unitOfMeasurementDto]
    )

    // У слотовой характеристики один диаметр приходит по разу на каждое
    // присоединение (`1:32`, `2:32`, ...), а модалка приоритетов показывает
    // схлопнутый список. Счётчик на кнопке считаем по тому же списку, иначе он
    // был бы больше в slotsCount раз, чем реально строк внутри.
    const optionsCount = useMemo(() => {
      if (type !== 'char') return 0
      const options = data?.options ?? []
      return isSlottedCharacteristicType(data?.type)
        ? collapseSlottedOptionsForPriority(options).length
        : options.length
    }, [type, data?.type, data?.options])

    if (!data) return null

    return (
      <div
        className={`${cn.sortableRow} ${isOverlay ? cn.overlayActive : ''} ${isChanged ? cn.changed : ''} ${!isConfigured ? cn.staticRow : ''}`}
        {...dragProps}
      >
        <div className={cn.dragHandle}>
          <HolderOutlined />
        </div>
        <div className={cn.rowInfo}>
          <div className={cn.nameWrapper}>
            <span className={cn.rowName}>
              {name}
              {unit}
            </span>
            {hasValueChanges && (
              <Tooltip title="В этой характеристике изменены приоритеты значений">
                <div className={cn.valueChangeBadge}>
                  <InfoCircleOutlined className={cn.valueChangeIcon} />
                  <span className={cn.valueChangeText}>Значения изменены</span>
                </div>
              </Tooltip>
            )}
          </div>
          {data.count !== undefined && (
            <span className={cn.rowCount}>({data.count})</span>
          )}
        </div>
        <div
          className={cn.rowActions}
          onPointerDown={(e) => e.stopPropagation()}
          onClick={(e) => e.stopPropagation()}
        >
          {type === 'char' && (
            <Tooltip title={`Настроить значения (${optionsCount})`}>
              <Button
                size="small"
                icon={<BarsOutlined />}
                onClick={() => onOpenValues?.(data)}
                className={cn.barsBtn}
              >
                {optionsCount}
              </Button>
            </Tooltip>
          )}
          {isConfigured ? (
            <>
              <Tag color={isChanged ? 'orange' : 'blue'} className={cn.prioTag}>
                Основной: {index + 1}
              </Tag>
              <Tooltip title="В остальные">
                <Button
                  type="text"
                  danger
                  size="small"
                  icon={<DeleteOutlined />}
                  onClick={() => onReset?.(data.code || data.value || '')}
                />
              </Tooltip>
            </>
          ) : (
            <>
              {getEffectivePriority(data) < MAX_PRIORITY && (
                <Tag
                  color={isChanged ? 'orange' : 'default'}
                  className={cn.prioTag}
                >
                  Приоритет: {index + 1}
                </Tag>
              )}
              {getEffectivePriority(data) < MAX_PRIORITY && (
                <Tooltip title="Сбросить приоритет">
                  <Button
                    type="text"
                    danger
                    size="small"
                    icon={<DeleteOutlined />}
                    onClick={() => onReset?.(data.code || data.value || '')}
                  />
                </Tooltip>
              )}
              <Tooltip title="В основные">
                <Button
                  type="text"
                  className={cn.addPrioBtn}
                  icon={<ArrowUpOutlined />}
                  onClick={() => onPrioritize?.(data.code || data.value || '')}
                />
              </Tooltip>
            </>
          )}
        </div>
      </div>
    )
  }
)

type SortableItemProps<T extends PriorityItem> = PriorityRowProps<T> & {
  id: string
  renderRow?(props: PriorityRowProps<T>): ReactNode
}

function SortableItemInner<T extends PriorityItem>({
  id,
  renderRow,
  ...props
}: SortableItemProps<T>) {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id })

  const style = useMemo(
    () => ({
      '--sortable-transform': CSS.Translate.toString(transform),
      '--sortable-transition': isDragging ? undefined : transition,
    }),
    [transform, transition, isDragging]
  )

  return (
    <div
      ref={setNodeRef}
      className={`${cn.sortableItem} ${isDragging ? cn.sortableItemDragging : ''}`}
      style={style}
    >
      {renderRow ? (
        renderRow({
          ...props,
          dragProps: { ...attributes, ...listeners },
        })
      ) : (
        <RowItem {...props} dragProps={{ ...attributes, ...listeners }} />
      )}
    </div>
  )
}

const SortableItem = memo(SortableItemInner) as typeof SortableItemInner

const DroppableSection = memo(
  ({
    id,
    title,
    children,
    count,
    onResetAll,
    showReset,
    showDropIndicator = true,
    forceDropIndicator = false,
  }: {
    id: string
    title: ReactNode
    children: ReactNode
    count: number
    onResetAll?: () => void
    showReset?: boolean
    showDropIndicator?: boolean
    forceDropIndicator?: boolean
  }) => {
    const { setNodeRef, isOver } = useDroppable({ id })

    return (
      <div ref={setNodeRef} className={cn.groupSection}>
        {(isOver || forceDropIndicator) && showDropIndicator && (
          <div className={cn.dropIndicator} />
        )}
        <div className={cn.sectionHeader}>
          <span>
            {title} ({count})
          </span>
          {showReset && count > 0 && (
            <Button
              type="text"
              size="small"
              icon={<ClearOutlined />}
              onClick={onResetAll}
              className={cn.bulkResetBtn}
            />
          )}
        </div>
        {children}
      </div>
    )
  }
)

function PriorityManagerInner<T extends PriorityItem>({
  items = [],
  initialItems = [],
  onDataChange,
  type,
  onOpenValues,
  onUndo,
  onRedo,
  canUndo,
  canRedo,
  valueChangesMap = {},
  renderRow,
  mainTitle = 'Основные',
  othersTitle = 'Остальные',
  mainEmptyText = 'Перетащите сюда для основных',
  othersEmptyText = 'Перетащите сюда для остальных',
  canEditPriority = true,
}: PriorityManagerProps<T>) {
  const [activeId, setActiveId] = useState<UniqueIdentifier | null>(null)
  const [activeZone, setActiveZone] = useState<string | null>(null)
  const [isPointerInsideMain, setIsPointerInsideMain] = useState(false)
  const [isPointerInsideOthers, setIsPointerInsideOthers] = useState(false)
  const [mainDropIndex, setMainDropIndex] = useState<number | null>(null)
  const dragFromZoneRef = useRef<string | null>(null)
  const mainDropIndexRef = useRef<number | null>(null)
  const mainParentRef = useRef<HTMLDivElement>(null)
  const othersParentRef = useRef<HTMLDivElement>(null)
  const autoScrollArmedRef = useRef(false)
  const autoScrollTimerRef = useRef<number | null>(null)
  const autoScrollIntervalRef = useRef<number | null>(null)
  const pointerRef = useRef<{ x: number; y: number } | null>(null)
  const pendingEdgeRef = useRef<string | null>(null)

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 8 } }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  )

  const { mainItems, otherItems } = useMemo(() => {
    const safeItems = Array.isArray(items) ? items : []
    const main = safeItems
      .filter((i) => i.isMain === true)
      .sort((a, b) => getEffectivePriority(a) - getEffectivePriority(b))
    const others = safeItems.filter((i) => i.isMain !== true)
    const prioritizedOthers = others
      .filter((i) => getEffectivePriority(i) < MAX_PRIORITY)
      .sort((a, b) => getEffectivePriority(a) - getEffectivePriority(b))
    const unprioritizedOthers = others.filter(
      (i) => getEffectivePriority(i) === MAX_PRIORITY
    )
    return {
      mainItems: main,
      otherItems: [...prioritizedOthers, ...unprioritizedOthers],
    }
  }, [items])

  const initialStateMap = useMemo(() => {
    const map = new Map<string, ReturnType<typeof getComparableState>>()
    initialItems.forEach((i) => map.set(getItemKey(i), getComparableState(i)))
    return map
  }, [initialItems])

  const { initialOthersOrder, initialOthersPrioritizedCount } = useMemo(() => {
    const safeInitial = Array.isArray(initialItems) ? initialItems : []
    const initialOthers = safeInitial
      .filter((i) => i.isMain !== true)
      .sort((a, b) => getEffectivePriority(a) - getEffectivePriority(b))
    return {
      initialOthersOrder: initialOthers.map((i) => getItemKey(i)),
      initialOthersPrioritizedCount: initialOthers.filter(
        (i) => getEffectivePriority(i) < MAX_PRIORITY
      ).length,
    }
  }, [initialItems])

  const mainVirtualizer = useVirtualizer({
    count: mainItems.length,
    getScrollElement: () => mainParentRef.current,
    estimateSize: () => ESTIMATED_ROW_HEIGHT,
    overscan: 5,
  })

  const othersVirtualizer = useVirtualizer({
    count: otherItems.length,
    getScrollElement: () => othersParentRef.current,
    estimateSize: () => ESTIMATED_ROW_HEIGHT,
    overscan: 5,
  })

  const activeItem = useMemo(
    () =>
      activeId
        ? items.find((i) => getItemKey(i) === activeId.toString())
        : null,
    [activeId, items]
  )

  const resolveZoneById = useCallback(
    (id: string) => {
      if (mainItems.some((i) => getItemKey(i) === id)) return MAIN_ZONE_ID
      if (otherItems.some((i) => getItemKey(i) === id)) return OTHERS_ZONE_ID
      return null
    },
    [mainItems, otherItems]
  )

  const stopAutoScrollLoop = useCallback(() => {
    if (autoScrollIntervalRef.current !== null) {
      window.clearInterval(autoScrollIntervalRef.current)
      autoScrollIntervalRef.current = null
    }
  }, [])

  const clearArmTimer = useCallback(() => {
    if (autoScrollTimerRef.current !== null) {
      window.clearTimeout(autoScrollTimerRef.current)
      autoScrollTimerRef.current = null
    }
  }, [])

  const disarmAutoScroll = useCallback(() => {
    autoScrollArmedRef.current = false
    dragFromZoneRef.current = null
    pendingEdgeRef.current = null
    pointerRef.current = null
    mainDropIndexRef.current = null
    setIsPointerInsideMain(false)
    setIsPointerInsideOthers(false)
    setMainDropIndex(null)
    clearArmTimer()
    stopAutoScrollLoop()
  }, [clearArmTimer, stopAutoScrollLoop])

  const armAdvancedDragMode = useCallback(
    (zoneId: string | null) => {
      dragFromZoneRef.current = zoneId
      autoScrollArmedRef.current = zoneId !== OTHERS_ZONE_ID
      pendingEdgeRef.current = null
      clearArmTimer()
      stopAutoScrollLoop()
      if (zoneId !== OTHERS_ZONE_ID) return
      autoScrollTimerRef.current = window.setTimeout(() => {
        if (dragFromZoneRef.current === OTHERS_ZONE_ID) {
          autoScrollArmedRef.current = true
        }
      }, AUTO_SCROLL_HOLD_MS)
    },
    [clearArmTimer, stopAutoScrollLoop]
  )

  const getEdgeTarget = useCallback((clientX: number, clientY: number) => {
    const entries: Array<[string, HTMLDivElement | null]> = [
      [MAIN_ZONE_ID, mainParentRef.current],
      [OTHERS_ZONE_ID, othersParentRef.current],
    ]

    for (const [zoneId, container] of entries) {
      if (!container) continue
      const rect = container.getBoundingClientRect()
      const isInsideX = clientX >= rect.left && clientX <= rect.right
      const isInsideY = clientY >= rect.top && clientY <= rect.bottom
      if (!isInsideX || !isInsideY) continue

      const distanceToTop = clientY - rect.top
      const distanceToBottom = rect.bottom - clientY
      const canScrollUp = container.scrollTop > 0
      const canScrollDown =
        container.scrollTop + container.clientHeight <
        container.scrollHeight - 1

      if (distanceToTop <= AUTO_SCROLL_EDGE_THRESHOLD && canScrollUp) {
        return {
          key: `${zoneId}:top`,
          zoneId,
          direction: 'top' as const,
          container,
          distance: distanceToTop,
        }
      }
      if (distanceToBottom <= AUTO_SCROLL_EDGE_THRESHOLD && canScrollDown) {
        return {
          key: `${zoneId}:bottom`,
          zoneId,
          direction: 'bottom' as const,
          container,
          distance: distanceToBottom,
        }
      }
    }

    return null
  }, [])

  const startAutoScrollLoop = useCallback(() => {
    if (autoScrollIntervalRef.current !== null) return

    autoScrollIntervalRef.current = window.setInterval(() => {
      if (!autoScrollArmedRef.current || !pointerRef.current) {
        return
      }

      const edgeTarget = getEdgeTarget(
        pointerRef.current.x,
        pointerRef.current.y
      )
      if (!edgeTarget) {
        pendingEdgeRef.current = null
        stopAutoScrollLoop()
        return
      }

      pendingEdgeRef.current = edgeTarget.key
      const ratio =
        1 -
        Math.min(edgeTarget.distance, AUTO_SCROLL_EDGE_THRESHOLD) /
          AUTO_SCROLL_EDGE_THRESHOLD
      const step = Math.max(
        4,
        Math.round(AUTO_SCROLL_STEP_MAX * (0.35 + ratio * 0.65))
      )
      edgeTarget.container.scrollTop +=
        edgeTarget.direction === 'bottom' ? step : -step
    }, AUTO_SCROLL_INTERVAL_MS)
  }, [getEdgeTarget, stopAutoScrollLoop])

  useEffect(() => {
    if (!activeId) {
      disarmAutoScroll()
      return
    }

    const handlePointerMove = (event: PointerEvent) => {
      pointerRef.current = { x: event.clientX, y: event.clientY }

      const mainRect = mainParentRef.current?.getBoundingClientRect()
      const isInsideMain =
        !!mainRect &&
        event.clientX >= mainRect.left &&
        event.clientX <= mainRect.right &&
        event.clientY >= mainRect.top &&
        event.clientY <= mainRect.bottom
      setIsPointerInsideMain((prev) =>
        prev === isInsideMain ? prev : isInsideMain
      )
      const nextMainDropIndex =
        dragFromZoneRef.current === OTHERS_ZONE_ID && isInsideMain
          ? Math.max(
              0,
              Math.min(
                mainItems.length,
                Math.round(
                  (event.clientY -
                    mainRect.top +
                    mainParentRef.current!.scrollTop) /
                    ESTIMATED_ROW_HEIGHT
                )
              )
            )
          : null
      mainDropIndexRef.current = nextMainDropIndex
      setMainDropIndex((prev) =>
        prev === nextMainDropIndex ? prev : nextMainDropIndex
      )

      const othersRect = othersParentRef.current?.getBoundingClientRect()
      const isInsideOthers =
        !!othersRect &&
        event.clientX >= othersRect.left &&
        event.clientX <= othersRect.right &&
        event.clientY >= othersRect.top &&
        event.clientY <= othersRect.bottom
      setIsPointerInsideOthers((prev) =>
        prev === isInsideOthers ? prev : isInsideOthers
      )

      const delayedMode = dragFromZoneRef.current === OTHERS_ZONE_ID
      const edgeTarget = getEdgeTarget(event.clientX, event.clientY)

      if (delayedMode) {
        if (!autoScrollArmedRef.current) {
          return
        }

        if (!edgeTarget || edgeTarget.zoneId !== OTHERS_ZONE_ID) {
          pendingEdgeRef.current = null
          stopAutoScrollLoop()
          return
        }

        pendingEdgeRef.current = edgeTarget.key
        startAutoScrollLoop()
        return
      }

      if (!edgeTarget) {
        pendingEdgeRef.current = null
        stopAutoScrollLoop()
        return
      }

      pendingEdgeRef.current = edgeTarget.key
      startAutoScrollLoop()
    }

    window.addEventListener('pointermove', handlePointerMove, {
      passive: true,
    })

    return () => {
      window.removeEventListener('pointermove', handlePointerMove)
      disarmAutoScroll()
    }
  }, [
    activeId,
    disarmAutoScroll,
    getEdgeTarget,
    mainItems.length,
    startAutoScrollLoop,
    stopAutoScrollLoop,
  ])

  const collisionDetection = useCallback<CollisionDetection>(
    (args) => pointerWithin(args),
    []
  )

  const rebuildItems = useCallback(
    (
      mainList: T[],
      othersList: T[],
      prioritizedOthersCountOverride?: number
    ): T[] => {
      const prioritizedOthersCount =
        prioritizedOthersCountOverride ??
        othersList.filter((i) => getEffectivePriority(i) < MAX_PRIORITY).length
      const clampedCount = Math.max(
        0,
        Math.min(prioritizedOthersCount, othersList.length)
      )

      const normalizedMain = mainList.map((item, idx) => ({
        ...item,
        isMain: true,
        priority: idx + 1,
      }))
      const normalizedOthers = othersList.map((item, idx) => ({
        ...item,
        isMain: false,
        priority: idx < clampedCount ? idx + 1 : MAX_PRIORITY,
      }))

      return [...normalizedMain, ...normalizedOthers]
    },
    []
  )

  const handleDragEnd = useCallback(
    (event: DragEndEvent) => {
      const { active, over } = event
      const previewMainDropIndex = mainDropIndexRef.current
      const delayedMode = dragFromZoneRef.current === OTHERS_ZONE_ID
      const isAdvancedDrag = !delayedMode || autoScrollArmedRef.current
      setActiveId(null)
      setActiveZone(null)
      disarmAutoScroll()
      if (!over && previewMainDropIndex === null) return

      const activeIdStr = active.id.toString()
      const overIdStr = over?.id.toString() ?? MAIN_ZONE_ID
      const mainIds = mainItems.map((i) => getItemKey(i))
      const otherIds = otherItems.map((i) => getItemKey(i))

      const activeFromMain = mainIds.includes(activeIdStr)
      const activeFromOthers = otherIds.includes(activeIdStr)
      if (!activeFromMain && !activeFromOthers) return
      const isDroppingIntoMain =
        activeFromOthers && previewMainDropIndex !== null

      if (!isAdvancedDrag) {
        if (
          activeFromOthers &&
          overIdStr === OTHERS_ZONE_ID &&
          !isDroppingIntoMain
        ) {
          return
        }
      }

      const overInMain =
        overIdStr === MAIN_ZONE_ID ||
        mainIds.includes(overIdStr) ||
        isDroppingIntoMain
      const overInOthers =
        !isDroppingIntoMain &&
        (overIdStr === OTHERS_ZONE_ID || otherIds.includes(overIdStr))
      if (!overInMain && !overInOthers) return

      const resolveInsertIndex = (arr: T[], targetId: string) => {
        const idx = arr.findIndex((i) => getItemKey(i) === targetId)
        return idx === -1 ? arr.length : idx
      }

      const moveWithinList = (arr: T[], sourceId: string, targetId: string) => {
        const fromIdx = arr.findIndex((i) => getItemKey(i) === sourceId)
        if (fromIdx === -1) return null
        const toIdx = resolveInsertIndex(arr, targetId)
        const safeToIdx = toIdx >= arr.length ? arr.length - 1 : toIdx
        if (safeToIdx === fromIdx) {
          return {
            fromIdx,
            safeToIdx,
            movedItem: arr[fromIdx],
            next: arr,
            moved: false,
          }
        }
        return {
          fromIdx,
          safeToIdx,
          movedItem: arr[fromIdx],
          next: arrayMove(arr, fromIdx, safeToIdx),
          moved: true,
        }
      }

      const removeById = (
        arr: T[],
        targetId: string
      ): { next: T[]; removed: T | null } => {
        const idx = arr.findIndex((i) => getItemKey(i) === targetId)
        if (idx === -1) return { next: [...arr], removed: null }
        const next = [...arr]
        const [removed] = next.splice(idx, 1)
        return { next, removed }
      }

      let nextMain = [...mainItems]
      let nextOthers = [...otherItems]

      if (activeFromMain && overInMain) {
        const movedMain = moveWithinList(nextMain, activeIdStr, overIdStr)
        if (!movedMain) return
        nextMain = movedMain.next
        onDataChange(rebuildItems(nextMain, nextOthers))
        return
      }

      if (activeFromOthers && overInOthers) {
        const movedOthers = moveWithinList(nextOthers, activeIdStr, overIdStr)
        if (!movedOthers) return
        let prioritizedOthersCountOverride: number | undefined

        if (movedOthers.moved) {
          const currentPrioritizedCount = nextOthers.filter(
            (i) => getEffectivePriority(i) < MAX_PRIORITY
          ).length
          const affectedEndIdx = Math.max(
            movedOthers.fromIdx,
            movedOthers.safeToIdx
          )
          const movementTouchesUnprioritizedZone =
            movedOthers.fromIdx >= currentPrioritizedCount ||
            movedOthers.safeToIdx >= currentPrioritizedCount ||
            (movedOthers.movedItem &&
              getEffectivePriority(movedOthers.movedItem) === MAX_PRIORITY)

          if (movementTouchesUnprioritizedZone) {
            // Если двигаем элемент в "Остальные", измененными считаем все
            // позиции в затронутом диапазоне (как в "Основные").
            prioritizedOthersCountOverride = Math.max(
              currentPrioritizedCount,
              affectedEndIdx + 1
            )
          }
        }
        nextOthers = movedOthers.next

        const nextOthersOrder = nextOthers.map((i) => getItemKey(i))
        const isRestoredToInitialOrder =
          nextOthersOrder.length === initialOthersOrder.length &&
          nextOthersOrder.every((id, idx) => id === initialOthersOrder[idx])

        if (isRestoredToInitialOrder) {
          prioritizedOthersCountOverride = initialOthersPrioritizedCount
        }

        onDataChange(
          rebuildItems(nextMain, nextOthers, prioritizedOthersCountOverride)
        )
        return
      }

      if (activeFromMain && overInOthers) {
        const removed = removeById(nextMain, activeIdStr)
        if (!removed.removed) return
        nextMain = removed.next
        const insertAt = resolveInsertIndex(nextOthers, overIdStr)
        const itemToMove = {
          ...removed.removed,
          isMain: false,
          priority: MAX_PRIORITY,
        }
        nextOthers.splice(insertAt, 0, itemToMove)
        onDataChange(rebuildItems(nextMain, nextOthers))
        return
      }

      if (activeFromOthers && overInMain) {
        const removed = removeById(nextOthers, activeIdStr)
        if (!removed.removed) return
        nextOthers = removed.next
        const insertAt =
          previewMainDropIndex ?? resolveInsertIndex(nextMain, overIdStr)
        const itemToMove = {
          ...removed.removed,
          isMain: true,
        }
        nextMain.splice(insertAt, 0, itemToMove)
        onDataChange(rebuildItems(nextMain, nextOthers))
      }
    },
    [
      disarmAutoScroll,
      initialOthersOrder,
      initialOthersPrioritizedCount,
      mainItems,
      onDataChange,
      otherItems,
      rebuildItems,
    ]
  )

  const handleReset = useCallback(
    (id: PriorityItemId) => {
      const idStr = id.toString()
      const mainIdx = mainItems.findIndex((i) => getItemKey(i) === idStr)
      if (mainIdx !== -1) {
        const nextMain = [...mainItems]
        const [removed] = nextMain.splice(mainIdx, 1)
        if (!removed) return
        const nextOthers = [
          ...otherItems,
          { ...removed, isMain: false, priority: MAX_PRIORITY },
        ]
        onDataChange(rebuildItems(nextMain, nextOthers))
        return
      }

      const otherIdx = otherItems.findIndex((i) => getItemKey(i) === idStr)
      if (otherIdx === -1) return
      const nextOthers = [...otherItems]
      const [removed] = nextOthers.splice(otherIdx, 1)
      if (!removed) return
      nextOthers.push({ ...removed, isMain: false, priority: MAX_PRIORITY })
      onDataChange(rebuildItems(mainItems, nextOthers))
    },
    [mainItems, otherItems, onDataChange, rebuildItems]
  )

  const handleResetAll = useCallback(() => {
    const demotedMain = mainItems.map((i) => ({
      ...i,
      isMain: false,
      priority: MAX_PRIORITY,
    }))
    onDataChange(rebuildItems([], [...otherItems, ...demotedMain]))
  }, [mainItems, otherItems, onDataChange, rebuildItems])

  const prioritizeItem = useCallback(
    (id: PriorityItemId) => {
      const idStr = id.toString()
      const otherIdx = otherItems.findIndex((i) => getItemKey(i) === idStr)
      if (otherIdx === -1) return
      const nextOthers = [...otherItems]
      const [item] = nextOthers.splice(otherIdx, 1)
      if (!item) return
      const nextMain = [...mainItems, { ...item, isMain: true }]
      onDataChange(rebuildItems(nextMain, nextOthers))
    },
    [mainItems, otherItems, onDataChange, rebuildItems]
  )

  const mainIds = useMemo(
    () => mainItems.map((i) => getItemKey(i)),
    [mainItems]
  )
  const otherIds = useMemo(
    () => otherItems.map((i) => getItemKey(i)),
    [otherItems]
  )
  const isPreviewingMainDrop = mainDropIndex !== null

  if (!items || items.length === 0) return null

  return (
    <div className={cn.managerWrapper}>
      <div className={cn.managerToolbar}>
        <div
          className={cn.historyGroup}
          onPointerDown={(e) => e.stopPropagation()}
        >
          <Button
            icon={<UndoOutlined />}
            disabled={!canUndo}
            onClick={onUndo}
            size="small"
          />
          <Button
            icon={<RedoOutlined />}
            disabled={!canRedo}
            onClick={onRedo}
            size="small"
          />
        </div>
      </div>
      <DndContext
        sensors={sensors}
        autoScroll={false}
        collisionDetection={collisionDetection}
        onDragStart={(e) => {
          const draggedId = e.active.id.toString()
          const zoneId = resolveZoneById(draggedId)
          const activator = e.activatorEvent as PointerEvent | undefined
          setActiveId(e.active.id)
          setActiveZone(zoneId)
          disarmAutoScroll()
          if (activator && typeof activator.clientX === 'number') {
            pointerRef.current = { x: activator.clientX, y: activator.clientY }
          }
          armAdvancedDragMode(zoneId)
        }}
        onDragCancel={() => {
          setActiveId(null)
          setActiveZone(null)
          disarmAutoScroll()
        }}
        onDragEnd={handleDragEnd}
        modifiers={[restrictToVerticalAxis]}
      >
        <div
          className={`${cn.dropZone} ${activeId ? cn.isDraggingGlobal : ''}`}
        >
          <DroppableSection
            id={MAIN_ZONE_ID}
            title={mainTitle}
            count={mainItems.length}
            onResetAll={handleResetAll}
            showReset={canEditPriority}
            showDropIndicator={
              activeZone !== null && activeZone !== MAIN_ZONE_ID
            }
            forceDropIndicator={activeId !== null && isPointerInsideMain}
          >
            <div
              ref={mainParentRef}
              data-priority-scroll="true"
              data-priority-zone="main"
              className={cn.scrollBoxVirtual}
              style={{
                '--scroll-box-height': `${
                  mainItems.length > 0
                    ? Math.min(
                        (mainItems.length + (isPreviewingMainDrop ? 1 : 0)) *
                          ESTIMATED_ROW_HEIGHT,
                        400
                      )
                    : 80
                }px`,
              }}
            >
              <div
                className={cn.virtualList}
                style={{
                  '--virtual-total-size': `${mainVirtualizer.getTotalSize() + (isPreviewingMainDrop ? ESTIMATED_ROW_HEIGHT : 0)}px`,
                }}
              >
                <SortableContext
                  items={mainIds}
                  strategy={verticalListSortingStrategy}
                >
                  {mainVirtualizer.getVirtualItems().map((virtualRow) => {
                    const item = mainItems[virtualRow.index]
                    if (!item) return null
                    const itemId = getItemKey(item)
                    const initialItemState = initialStateMap.get(itemId) || {
                      isMain: false,
                      priority: undefined,
                    }
                    return (
                      <div
                        key={virtualRow.key}
                        className={`${cn.virtualItem} ${cn.virtualItemAnimated}`}
                        style={{
                          '--virtual-row-size': `${virtualRow.size}px`,
                          '--virtual-row-start': `${virtualRow.start + (isPreviewingMainDrop && mainDropIndex !== null && virtualRow.index >= mainDropIndex ? ESTIMATED_ROW_HEIGHT : 0)}px`,
                        }}
                      >
                        <SortableItem
                          id={itemId}
                          data={item}
                          index={virtualRow.index}
                          isConfigured
                          isChanged={hasComparableStateChanged(
                            getComparableState(item),
                            initialItemState
                          )}
                          hasValueChanges={
                            item.code ? !!valueChangesMap[item.code] : false
                          }
                          onReset={handleReset}
                          type={type}
                          onOpenValues={onOpenValues}
                          renderRow={renderRow}
                        />
                      </div>
                    )
                  })}
                </SortableContext>
              </div>
              {mainItems.length === 0 && (
                <div className={cn.emptyPlaceholder}>{mainEmptyText}</div>
              )}
            </div>
          </DroppableSection>

          <Divider className={cn.zonesDivider} />
          <DroppableSection
            id={OTHERS_ZONE_ID}
            title={othersTitle}
            count={otherItems.length}
            showDropIndicator={activeZone !== null}
            forceDropIndicator={activeId !== null && isPointerInsideOthers}
          >
            <div
              ref={othersParentRef}
              data-priority-scroll="true"
              data-priority-zone="others"
              className={cn.scrollBoxVirtual}
              style={{
                '--scroll-box-height': `${
                  otherItems.length > 0
                    ? Math.min(otherItems.length * ESTIMATED_ROW_HEIGHT, 400)
                    : 80
                }px`,
              }}
            >
              <div
                className={cn.virtualList}
                style={{
                  '--virtual-total-size': `${othersVirtualizer.getTotalSize()}px`,
                }}
              >
                <SortableContext
                  items={otherIds}
                  strategy={verticalListSortingStrategy}
                >
                  {othersVirtualizer.getVirtualItems().map((virtualRow) => {
                    const item = otherItems[virtualRow.index]
                    if (!item) return null
                    const itemId = getItemKey(item)
                    const initialItemState = initialStateMap.get(itemId) || {
                      isMain: false,
                      priority: undefined,
                    }
                    return (
                      <div
                        key={virtualRow.key}
                        className={cn.virtualItem}
                        style={{
                          '--virtual-row-size': `${virtualRow.size}px`,
                          '--virtual-row-start': `${virtualRow.start}px`,
                        }}
                      >
                        <SortableItem
                          id={itemId}
                          data={item}
                          index={virtualRow.index}
                          isConfigured={false}
                          isChanged={hasComparableStateChanged(
                            getComparableState(item),
                            initialItemState
                          )}
                          hasValueChanges={
                            item.code ? !!valueChangesMap[item.code] : false
                          }
                          onReset={handleReset}
                          type={type}
                          onOpenValues={onOpenValues}
                          onPrioritize={prioritizeItem}
                          renderRow={renderRow}
                        />
                      </div>
                    )
                  })}
                </SortableContext>
              </div>
              {otherItems.length === 0 && (
                <div className={cn.emptyPlaceholder}>{othersEmptyText}</div>
              )}
            </div>
          </DroppableSection>
        </div>

        <DragOverlay
          dropAnimation={{
            duration: 150,
            easing: 'cubic-bezier(0.18, 0.67, 0.6, 1.22)',
            sideEffects: defaultDropAnimationSideEffects({
              styles: { active: { opacity: '0.4' } },
            }),
          }}
        >
          {activeItem ? (
            renderRow ? (
              renderRow({
                data: activeItem,
                type,
                isOverlay: true,
                isConfigured: activeItem.isMain === true,
                index: mainItems.findIndex(
                  (c) => getItemKey(c) === getItemKey(activeItem)
                ),
                onReset: handleReset,
                onPrioritize: prioritizeItem,
              })
            ) : (
              <RowItem
                data={activeItem}
                type={type}
                isOverlay
                isConfigured={activeItem.isMain === true}
                index={mainItems.findIndex(
                  (c) => getItemKey(c) === getItemKey(activeItem)
                )}
              />
            )
          ) : null}
        </DragOverlay>
      </DndContext>
    </div>
  )
}

export const PriorityManager = memo(
  PriorityManagerInner
) as typeof PriorityManagerInner

const ManualPriorityConfig: React.FC = () => {
  const [loading, setLoading] = useState(false)
  const [trees, setTrees] = useState<PriorityCategoryTree[]>([])
  const [history, setHistory] = useState<Record<string, PriorityHistoryEntry>>(
    {}
  )
  const [globalHistoryStack, setGlobalHistoryStack] = useState<string[]>([])
  const [globalRedoStack, setGlobalRedoStack] = useState<string[]>([])
  const [modalVisible, setModalVisible] = useState(false)
  const [modalContent, setModalContent] =
    useState<PriorityValuesModalContent | null>(null)
  const [modal, contextHolder] = Modal.useModal()
  const { selectedOrganisation } = useUserInfoStore()

  const fetchData = useCallback(
    async (showLoader = true) => {
      if (showLoader) setLoading(true)
      try {
        const response = await api.get<PriorityCategoryTreesResponse>(
          '/categories/root-trees-with-characteristics'
        )
        setTrees(response.data.trees || [])
      } catch (error) {
        handleApiError(error, modal)
      } finally {
        if (showLoader) setLoading(false)
      }
    },
    [modal]
  )

  useEffect(() => {
    fetchData()
  }, [fetchData])

  const handleStateChange = useCallback(
    (key: string, nextPresent: PriorityConfigItem[]) => {
      setHistory((prev) => {
        const curr = prev[key] || {
          initial: [],
          present: [],
          past: [],
          future: [],
        }
        return {
          ...prev,
          [key]: {
            ...curr,
            past: [...curr.past, curr.present],
            present: nextPresent,
            future: [],
          },
        }
      })
      setGlobalHistoryStack((prev) => [...prev, key])
      setGlobalRedoStack([])
    },
    []
  )

  const undo = useCallback(
    (key?: string) => {
      const targetKey = key || globalHistoryStack[globalHistoryStack.length - 1]
      if (!targetKey) return
      setHistory((prev) => {
        const state = prev[targetKey]
        if (!state || state.past.length === 0) return prev
        return {
          ...prev,
          [targetKey]: {
            ...state,
            past: state.past.slice(0, -1),
            present: state.past[state.past.length - 1],
            future: [state.present, ...state.future],
          },
        }
      })
      if (key) {
        setGlobalHistoryStack((prev) => {
          const n = [...prev]
          const i = n.lastIndexOf(key)
          if (i !== -1) n.splice(i, 1)
          return n
        })
        setGlobalRedoStack((prev) => [key, ...prev])
      } else {
        setGlobalRedoStack((prev) => [targetKey, ...prev])
        setGlobalHistoryStack((prev) => prev.slice(0, -1))
      }
    },
    [globalHistoryStack]
  )

  const redo = useCallback(
    (key?: string) => {
      const targetKey = key || globalRedoStack[0]
      if (!targetKey) return
      setHistory((prev) => {
        const state = prev[targetKey]
        if (!state || state.future.length === 0) return prev
        return {
          ...prev,
          [targetKey]: {
            ...state,
            past: [...state.past, state.present],
            present: state.future[0],
            future: state.future.slice(1),
          },
        }
      })
      if (key) {
        setGlobalRedoStack((prev) => {
          const n = [...prev]
          const i = n.indexOf(key)
          if (i !== -1) n.splice(i, 1)
          return n
        })
        setGlobalHistoryStack((prev) => [...prev, key])
      } else {
        setGlobalHistoryStack((prev) => [...prev, targetKey])
        setGlobalRedoStack((prev) => prev.slice(1))
      }
    },
    [globalRedoStack]
  )

  const handleSave = useCallback(async () => {
    const categoryUpdates: CategoryPriorityUpdate[] = []
    const valueUpdates: ValuePriorityUpdate[] = []

    Object.entries(history).forEach(([key, state]) => {
      if (!state?.initial || !state?.present) return

      if (key.startsWith('cat_')) {
        const categoryId = key.replace('cat_', '')
        const initialMap = new Map<
          string,
          { isMain: boolean; priority?: number }
        >()
        state.initial
          .filter(isPriorityCharacteristic)
          .forEach((i) =>
            initialMap.set(i.code.toString(), getComparableState(i))
          )

        state.present.filter(isPriorityCharacteristic).forEach((item) => {
          const currentState = getComparableState(item)
          const originalState = initialMap.get(item.code.toString()) || {
            isMain: false,
            priority: undefined,
          }

          if (hasComparableStateChanged(currentState, originalState)) {
            const update: CategoryPriorityUpdate = {
              categoryId,
              characteristicCode: item.code,
              isMain: currentState.isMain,
            }
            if (currentState.priority !== undefined) {
              update.priority = currentState.priority
            }
            categoryUpdates.push(update)
          }
        })
      } else if (key.startsWith('val_')) {
        const parts = key.split('_')
        const charCode = parseInt(parts[2], 10)
        if (Number.isNaN(charCode)) return

        const initialMap = new Map<
          string,
          { isMain: boolean; priority?: number }
        >()
        state.initial
          .filter(isPriorityValueOption)
          .forEach((i) => initialMap.set(i.value, getComparableState(i)))

        state.present.filter(isPriorityValueOption).forEach((item) => {
          const currentState = getComparableState(item)
          const originalState = initialMap.get(item.value) || {
            isMain: false,
            priority: undefined,
          }

          if (hasComparableStateChanged(currentState, originalState)) {
            const update: ValuePriorityUpdate = {
              characteristicCode: charCode,
              value: item.value,
              isMain: currentState.isMain,
            }
            if (currentState.priority !== undefined) {
              update.priority = currentState.priority
            }
            valueUpdates.push(update)
          }
        })
      }
    })

    const payload = {
      resetCategories: [],
      resetValuesByChrcCode: [],
      categoryUpdates,
      valueUpdates,
    }

    const t = toast.loading('Сохранение...')
    setLoading(true)
    try {
      await api.post('/catalog/priorities/configuration', payload)
      await fetchData(false)
      setHistory({})
      setGlobalHistoryStack([])
      setGlobalRedoStack([])
      toast.success('Успешно обновлено!', { id: t })
    } catch (e) {
      toast.error('Ошибка при сохранении', { id: t })
      handleApiError(e, modal)
    } finally {
      setLoading(false)
    }
  }, [history, fetchData, modal])

  const openValuesModal = useCallback(
    (char: PriorityCharacteristic, categoryId: string) => {
      const vKey = `val_${categoryId}_${char.code}`

      // У слотовой характеристики один и тот же диаметр приходит по разу на каждое
      // присоединение (`1:32`, `2:32`, ...). Приоритет при этом глобальный и
      // задаётся по чистому значению, поэтому список схлопываем — иначе он
      // дублировался бы, а на бэк ушла бы строка с префиксом слота.
      const options = normalizePriorityOrder(
        isSlottedCharacteristicType(char.type)
          ? collapseSlottedOptionsForPriority(char.options || [])
          : char.options || []
      )

      setHistory((prev) =>
        prev[vKey]
          ? prev
          : {
              ...prev,
              [vKey]: {
                initial: options,
                present: options,
                past: [],
                future: [],
              },
            }
      )
      setModalContent({ ...char, categoryId, options })
      setModalVisible(true)
    },
    []
  )

  const valueChangesMap = useMemo(() => {
    const map: Record<string, Record<number, boolean>> = {}

    Object.entries(history).forEach(([key, state]) => {
      if (key.startsWith('val_')) {
        const parts = key.split('_')
        if (parts.length < 3) return

        const catId = parts[1]
        const charCode = parseInt(parts[2], 10)
        if (Number.isNaN(charCode)) return

        const initialMap = new Map<
          string,
          { isMain: boolean; priority?: number }
        >()
        state.initial
          .filter(isPriorityValueOption)
          .forEach((i) => initialMap.set(i.value, getComparableState(i)))

        const hasChanges = state.present
          .filter(isPriorityValueOption)
          .some((item) => {
            const currentState = getComparableState(item)
            const originalState = initialMap.get(item.value) || {
              isMain: false,
              priority: undefined,
            }
            return hasComparableStateChanged(currentState, originalState)
          })

        if (hasChanges) {
          if (!map[catId]) map[catId] = {}
          map[catId][charCode] = true
        }
      }
    })
    return map
  }, [history])

  const renderTree = useCallback(
    (nodes: PriorityCategoryTree[]): ReactNode => (
      <Collapse ghost expandIconPosition="end" className={cn.mainCollapse}>
        {nodes.map((node) => {
          const hKey = `cat_${node.category.id}`
          const currentCatId = node.category.id
          const initialCharacteristics: PriorityConfigItem[] =
            normalizePriorityOrder(node.characteristics || [])

          return (
            <Collapse.Panel
              key={currentCatId}
              header={
                <div className={cn.catHeader}>
                  <span className={cn.catName}>
                    {cleanTechnicalSuffix(node.category.description)}
                    <span className={cn.catId}>#{node.category.code}</span>
                  </span>
                </div>
              }
            >
              <div className={cn.panelContent}>
                <PriorityManager
                  type="char"
                  items={history[hKey]?.present || initialCharacteristics}
                  initialItems={initialCharacteristics}
                  canUndo={(history[hKey]?.past.length ?? 0) > 0}
                  canRedo={(history[hKey]?.future.length ?? 0) > 0}
                  onUndo={() => undo(hKey)}
                  onRedo={() => redo(hKey)}
                  valueChangesMap={valueChangesMap[currentCatId] || {}}
                  onDataChange={(newData) => {
                    if (!history[hKey]) {
                      setHistory((prev) => ({
                        ...prev,
                        [hKey]: {
                          initial: initialCharacteristics,
                          present: newData,
                          past: [initialCharacteristics],
                          future: [],
                        },
                      }))
                      setGlobalHistoryStack((prev) => [...prev, hKey])
                      setGlobalRedoStack([])
                    } else handleStateChange(hKey, newData)
                  }}
                  onOpenValues={(item) => {
                    if (isPriorityCharacteristic(item))
                      openValuesModal(item, currentCatId)
                  }}
                />
                {!!node.childCategories?.length && (
                  <div className={cn.nestedTree}>
                    {renderTree(node.childCategories)}
                  </div>
                )}
              </div>
            </Collapse.Panel>
          )
        })}
      </Collapse>
    ),
    [history, undo, redo, handleStateChange, openValuesModal, valueChangesMap]
  )

  const totalCount = useMemo(() => {
    let count = 0
    if (!history) return 0
    Object.values(history).forEach((s) => {
      if (!s || !s.initial || !s.present) return
      const m = new Map<string, { isMain: boolean; priority?: number }>()
      s.initial.forEach((i) => m.set(getItemKey(i), getComparableState(i)))
      s.present.forEach((item) => {
        const key = getItemKey(item)
        const currentState = getComparableState(item)
        const originalState = m.get(key) || {
          isMain: false,
          priority: undefined,
        }
        if (hasComparableStateChanged(currentState, originalState)) {
          count++
        }
      })
    })
    return count
  }, [history])

  return (
    <div
      className={`${cn.container} ${Object.keys(selectedOrganisation).length > 0 ? cn.blueTheme : cn.orangeTheme}`}
    >
      {contextHolder}
      <Loader loaderType="modal" isModalOpen={loading} />
      <div className={cn.treeWrapper}>
        {trees.length > 0
          ? renderTree(trees)
          : !loading && <div className={cn.noData}>Данные отсутствуют</div>}
      </div>
      <Modal
        title={`Значения: ${modalContent ? cleanTechnicalSuffix(modalContent.name) : ''}`}
        open={modalVisible}
        onCancel={() => setModalVisible(false)}
        footer={null}
        width={800}
        centered
        className={cn.customModal}
        destroyOnHidden
      >
        {modalContent && (
          <div className={cn.modalBody}>
            <div className={cn.modalMeta}>
              <Tag color="orange">
                Тип:{' '}
                {(modalContent.type && TYPE_LABELS[modalContent.type]) ||
                  modalContent.type}
              </Tag>
              <Tag color="blue">
                Ед. изм: {modalContent.unitOfMeasurementDto?.description || '—'}
              </Tag>
            </div>
            {isSlottedCharacteristicType(modalContent.type) && (
              <div className={cn.slottedPriorityHint}>
                Приоритет значения действует сразу на все присоединения — задать
                его отдельно для первого или второго нельзя.
              </div>
            )}
            <PriorityManager
              type="value"
              items={
                history[`val_${modalContent.categoryId}_${modalContent.code}`]
                  ?.present || []
              }
              initialItems={
                history[`val_${modalContent.categoryId}_${modalContent.code}`]
                  ?.initial || []
              }
              canUndo={
                (history[`val_${modalContent.categoryId}_${modalContent.code}`]
                  ?.past.length ?? 0) > 0
              }
              canRedo={
                (history[`val_${modalContent.categoryId}_${modalContent.code}`]
                  ?.future.length ?? 0) > 0
              }
              onUndo={() =>
                undo(`val_${modalContent.categoryId}_${modalContent.code}`)
              }
              onRedo={() =>
                redo(`val_${modalContent.categoryId}_${modalContent.code}`)
              }
              onDataChange={(n) =>
                handleStateChange(
                  `val_${modalContent.categoryId}_${modalContent.code}`,
                  n
                )
              }
            />
          </div>
        )}
      </Modal>
      <PrioritySaveBar
        changeCount={totalCount}
        onSave={handleSave}
        onUndo={() => undo()}
        onRedo={() => redo()}
        canUndo={globalHistoryStack.length > 0}
        canRedo={globalRedoStack.length > 0}
        loading={loading}
        isBlueTheme={Object.keys(selectedOrganisation).length > 0}
      />
    </div>
  )
}

export default ManualPriorityConfig
